import { dayjs, formatDate } from '@koob/dates';
import { getSetting, getSettingLabel } from '@koob/utils';
import { decodeId } from 'utils';
import ExportFlowPage from './ExportFlowPage';

function formatAmount(amount, currency) {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency || 'EUR',
    maximumFractionDigits: 0,
  }).format(amount ?? 0);
}

function buildDayEntries({ day, experiences, hotels, labels }) {
  const entries = [];

  const experience = experiences?.find(({ id }) => id === day?.experienceId);
  if (experience?.name) {
    entries.push({
      kind: getSettingLabel(labels, 'pricing-daily-kind-program'),
      name: experience.name,
    });
  }

  (day?.selectedExtras ?? []).forEach((extra) => {
    if (!extra?.name) return;
    entries.push({
      kind: getSettingLabel(labels, 'pricing-daily-kind-service'),
      name: extra.name,
    });
  });

  const hotelId = decodeId(day?.hotelId);
  const hotel = hotels?.find(
    ({ id }) => id?.toString() === (hotelId ?? '').toString(),
  );
  const hotelName = hotel?.displayName ?? hotel?.name;
  if (hotelName) {
    entries.push({
      kind: getSettingLabel(labels, 'pricing-daily-kind-hotel'),
      name: hotelName,
    });
  }

  return entries;
}

export default function ExportPricingDaily({
  trip,
  tourOperator,
  settings,
  pricing,
  days,
  experiences,
  hotels,
}) {
  const pricingSection = getSetting(settings, 'pricing');
  const labels = pricingSection?.sections;
  const dayLabel =
    getSettingLabel(
      getSetting(getSetting(settings, 'days')?.sections, 'program-of-the-day')
        ?.fields,
      'pod-day-number',
    ) || 'Day';

  const rows = (days ?? trip?.tripDays ?? []).map((day, index) => ({
    index,
    date: dayjs(trip?.startDate).add(index, 'days'),
    entries: buildDayEntries({ day, experiences, hotels, labels }),
  }));

  const filled = rows.filter(({ entries }) => entries.length > 0);
  if (filled.length === 0) return null;

  const totalRow = (
    <div className="mt-6 flex items-center justify-between border-t-2 border-[var(--td-ink)] pt-3">
      <div className="text-sm font-semibold uppercase tracking-wide">
        {[
          getSettingLabel(labels, 'pricing-total'),
          '·',
          pricing.travellerCount,
          getSettingLabel(labels, 'pricing-travellers'),
        ].join(' ')}
      </div>
      <div
        className="text-2xl"
        style={{ fontFamily: 'var(--td-font-display)' }}
      >
        {formatAmount(pricing.grandTotal, pricing.currency)}
      </div>
    </div>
  );

  return (
    <ExportFlowPage
      sectionLabel={pricingSection?.displayName}
      title={getSettingLabel(labels, 'pricing-daily-title')}
      trip={trip}
      tourOperator={tourOperator}
      settings={settings}
      footerStatus
    >
      <div className="mt-4 flex flex-col gap-4">
        {filled.map(({ index, date, entries }, position) => (
          <div key={index} data-prevent-cut>
            <div className="text-2xs font-bold uppercase tracking-widest text-[var(--td-primary)]">
              {`${dayLabel} ${index + 1} · ${formatDate(date, 'DD MMM YYYY')}`}
            </div>

            <div className="mt-1 divide-y divide-k-gray/40 border-t border-k-gray/40">
              {entries.map((entry, entryIndex) => (
                <div
                  key={entryIndex}
                  className="flex items-baseline gap-3 py-1.5"
                >
                  <div className="w-32 flex-shrink-0 text-2xs uppercase tracking-wide text-[var(--td-ink-muted)]">
                    {entry.kind}
                  </div>
                  <div className="text-sm leading-snug">{entry.name}</div>
                </div>
              ))}
            </div>

            {position === filled.length - 1 && totalRow}
          </div>
        ))}
      </div>
    </ExportFlowPage>
  );
}
